import { motion } from "framer-motion";
import { Phone, ArrowRight, Check, MapPin, BadgeCheck, CalendarCheck, MessageSquare, Camera } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { CampaignQuoteForm } from "@/components/CampaignQuoteForm";
import { SITE, IMAGES } from "@/content/site";

const AREAS = [
  "Tierra Verde",
  "Bayway Isles",
  "Isla del Sol",
  "St. Petersburg",
  "Pinellas Bayway Corridor",
  "All of ZIP 33715",
];

const INCLUDED = [
  "Water testing & chemical balancing every visit",
  "Brushing walls, steps & waterline",
  "Skimming the surface",
  "Vacuuming as needed",
  "Emptying skimmer & pump baskets",
  "Equipment inspection on every visit",
  "Filter maintenance on schedule",
  "Salt system maintenance for salt pools",
  "Weekly service reports with photos",
];

const WHY = [
  {
    icon: CalendarCheck,
    title: "Reliable, consistent service day",
    text: "Your pool is serviced on the same day, on a consistent route, every week — you'll never wonder if anyone showed up.",
  },
  {
    icon: Camera,
    title: "Communication you can see",
    text: "Weekly service reports with photos after every visit, so you always know exactly what was done and what your water needs.",
  },
  {
    icon: BadgeCheck,
    title: "CPO certified professional chemistry",
    text: "Certified Pool Operator standards on every reading and adjustment — protecting your swimmers, your surface, and your equipment.",
  },
  {
    icon: MessageSquare,
    title: "A local owner you can reach",
    text: "We're not the cheapest pool company — we're the one that shows up, communicates, and does what we say we'll do. You talk directly to the owner.",
  },
];

export default function TierraVerdePage() {
  return (
    <>
      <SEO
        title="Pool Service in Tierra Verde & St. Petersburg FL (33715) | Point of Pines Pools"
        description="Reliable weekly pool service for Tierra Verde, Bayway Isles, Isla del Sol, St. Petersburg and 33715 homes. Locally owned, CPO certified, weekly photo reports. Call (727) 744-5908 or get a quote."
        path="/service-areas/tierra-verde-fl/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Residential Pool Service",
          provider: {
            "@type": "HomeAndConstructionBusiness",
            name: SITE.name,
            telephone: "+1-727-744-5908",
            url: SITE.domain,
          },
          areaServed: [
            { "@type": "Place", name: "Tierra Verde, FL" },
            { "@type": "Place", name: "Bayway Isles, St. Petersburg, FL" },
            { "@type": "Place", name: "Isla del Sol, St. Petersburg, FL" },
            { "@type": "City", name: "St. Petersburg, FL" },
            { "@type": "PostalCode", postalCode: "33715" },
          ],
        }}
      />

      <section data-testid="tv-hero" className="relative overflow-hidden bg-gradient-to-br from-navy via-deepsea to-ocean pt-32 pb-16 sm:pt-40 sm:pb-20 lg:pb-24">
        <div className="noise-overlay absolute inset-0 opacity-[0.05] pointer-events-none" />
        <div className="absolute -top-32 -right-32 w-[30rem] h-[30rem] rounded-full bg-lagoon/15 blur-[110px] pointer-events-none" />
        <div className="relative px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            <div className="lg:col-span-6">
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.1 }}
                className="text-xs font-mono uppercase tracking-[0.25em] text-sky2 font-semibold"
                data-testid="tv-hero-overline"
              >
                Weekly Pool Service — Tierra Verde &amp; St. Pete · 33715
              </motion.p>
              <h1 className="mt-5 font-display font-extrabold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-[3.4rem] text-white" data-testid="tv-hero-title">
                <MaskedLine delay={0.2}>Reliable Pool Service</MaskedLine>
                <MaskedLine delay={0.35} className="text-transparent bg-clip-text bg-gradient-to-r from-sky2 via-aqua to-lagoonlight">
                  in Tierra Verde &amp; St.&nbsp;Pete
                </MaskedLine>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, delay: 0.6 }}
                className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl"
                data-testid="tv-hero-subcopy"
              >
                Professional weekly pool care serving Tierra Verde, Bayway Isles, Isla del Sol,
                the 33715 waterfront and all of St. Petersburg — from a locally owned, CPO certified
                Pinellas County company that shows up and communicates.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, delay: 0.75 }}
                className="mt-8 flex flex-col sm:flex-row gap-3.5"
              >
                <a
                  href="#quote-form"
                  data-testid="tv-hero-quote-button"
                  className="group inline-flex items-center justify-center gap-2.5 bg-aqua text-navy font-bold px-8 py-4 rounded-full transition-all duration-300 hover:bg-sky2 hover:text-navy hover:shadow-xl hover:shadow-cyan-500/30"
                >
                  Get a Pool Service Quote
                  <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <a
                  href={SITE.phoneHref}
                  data-testid="tv-hero-call-button"
                  className="inline-flex items-center justify-center gap-2.5 border-2 border-white/25 text-white font-bold px-8 py-4 rounded-full transition-all duration-300 hover:bg-white hover:text-navy"
                >
                  <Phone className="w-5 h-5" /> {SITE.phone}
                </a>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 1 }}
                className="mt-8 flex flex-wrap gap-x-6 gap-y-2.5"
                data-testid="tv-hero-trust-chips"
              >
                {["Locally Owned in Pinellas County", "CPO Certified", "Weekly Photo Reports"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-lagoonlight" /> {t}
                  </span>
                ))}
              </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-6"
            >
              <CampaignQuoteForm />
            </motion.div>
          </div>
        </div>
      </section>

      <div data-testid="tv-mailer-note" className="bg-mist border-b border-slate-200/70">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-4 flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-lagoon shrink-0" />
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-navy">Scanned our postcard? You&apos;re in the right place.</span>{" "}
            We already service pools across Tierra Verde, Bayway Isles, Isla del Sol, the Pinellas
            Bayway corridor and greater St. Pete — mention the postcard when you reach out.
          </p>
        </div>
      </div>

      <section data-testid="tv-areas-section" className="py-14 sm:py-20 bg-white">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-pool font-semibold">Your Neighborhood Is On Our Route</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl font-bold tracking-tight leading-[1.15] text-navy">
              Pool service across the 33715 waterfront
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              From the estates of Tierra Verde to the island homes of Isla del Sol and the waterfront
              streets of Bayway Isles along the Pinellas Bayway — and everywhere else in St. Pete —
              this is our backyard too.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-9 flex flex-wrap justify-center gap-2.5" data-testid="tv-area-chips">
              {AREAS.map((a) => (
                <span key={a} className="inline-flex items-center gap-2 bg-mist border border-slate-200 rounded-full px-4 py-2.5 text-sm font-semibold text-navy">
                  <MapPin className="w-4 h-4 text-lagoon" /> {a}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="tv-included-section" className="py-16 sm:py-24 bg-shell">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-pool font-semibold">Full-Service Weekly Maintenance</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-navy">
              One weekly visit. Everything handled.
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Island and waterfront pools work harder — salt air, bay breeze, and Florida sun burn
              through chemistry fast. Here&apos;s what every weekly visit covers, whether you&apos;re
              on the water in Tierra Verde or in a neighborhood anywhere across St. Pete:
            </p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {INCLUDED.map((item, i) => (
              <motion.div
                key={item}
                data-testid={`tv-included-${i}`}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.07 }}
                className="flex items-start gap-3.5 bg-white rounded-xl border border-slate-200/80 p-5"
              >
                <span className="w-7 h-7 rounded-full bg-emerald-50 flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-lagoon" />
                </span>
                <span className="text-sm sm:text-[15px] text-slate-700 leading-relaxed">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="tv-why-section" className="py-16 sm:py-24 bg-gradient-to-br from-deeppine via-pine to-[#0F3D30] text-white relative overflow-hidden">
        <div className="noise-overlay absolute inset-0 opacity-[0.04] pointer-events-none" />
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-lagoonlight font-semibold">Why Point of Pines</p>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15]">
                Not the cheapest. The most dependable.
              </h2>
              <p className="mt-5 text-base text-emerald-50/70 leading-relaxed">
                We&apos;re a locally owned Pinellas County pool company built around four things:
                reliability, consistency, communication, and professional-grade work. If you&apos;ve
                been burned by pool companies that rotate strangers through your backyard or go quiet
                for weeks — that&apos;s exactly why we exist.
              </p>
            </Reveal>
            <div className="mt-8 space-y-6">
              {WHY.map((w, i) => (
                <Reveal key={w.title} delay={i * 0.06}>
                  <div data-testid={`tv-why-${i}`} className="flex gap-4">
                    <span className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                      <w.icon className="w-5 h-5 text-lagoonlight" />
                    </span>
                    <div>
                      <h3 className="font-display text-lg font-semibold tracking-tight">{w.title}</h3>
                      <p className="mt-1.5 text-sm text-emerald-50/70 leading-relaxed">{w.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <div className="clip-arch overflow-hidden ring-1 ring-white/15 shadow-2xl shadow-black/30">
                <img
                  src={IMAGES.rentalBeachPool}
                  alt="Waterfront pool and spa in the Tierra Verde and St Petersburg FL area serviced weekly by Point of Pines Pools"
                  className="w-full h-80 sm:h-[26rem] object-cover"
                  loading="lazy"
                  data-testid="tv-why-photo"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="tv-bottom-cta" className="py-16 sm:py-20 max-lg:pb-32 bg-white">
        <div className="px-4 sm:px-6 lg:px-12 max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-navy">
              Ready for a pool you never have to think about?
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Get a no-pressure quote for weekly service at your Tierra Verde, Bayway Isles, Isla del
              Sol, or St. Pete home. Most quotes are turned around the same day.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3.5">
              <a
                href="#quote-form"
                data-testid="tv-bottom-quote-button"
                className="group inline-flex items-center justify-center gap-2.5 bg-navy text-white font-bold px-8 py-4 rounded-full transition-all duration-300 hover:bg-pool hover:text-white hover:shadow-lg hover:shadow-cyan-500/25"
              >
                Get a Pool Service Quote
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a
                href={SITE.phoneHref}
                data-testid="tv-bottom-call-button"
                className="inline-flex items-center justify-center gap-2.5 border-2 border-navy text-navy font-bold px-8 py-4 rounded-full transition-all duration-300 hover:bg-navy hover:text-white"
              >
                <Phone className="w-5 h-5" /> {SITE.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <div
        data-testid="tv-mobile-sticky-bar"
        className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-navy/95 backdrop-blur-md border-t border-white/10 px-4 py-3 grid grid-cols-2 gap-3"
      >
        <a
          href={SITE.phoneHref}
          data-testid="tv-sticky-call-button"
          className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white font-bold py-3 rounded-full text-sm"
        >
          <Phone className="w-4 h-4" /> {SITE.phone}
        </a>
        <a
          href="#quote-form"
          data-testid="tv-sticky-quote-button"
          className="inline-flex items-center justify-center gap-2 bg-aqua text-navy font-bold py-3 rounded-full text-sm"
        >
          Get a Quote
        </a>
      </div>
    </>
  );
}
